<template>
    <TabView
        v-bind="$attrs"
        v-model:activeIndex="activeIndex"
        :pt="passThrough"
    >
        <template
            v-for="(tabItem, index) in panels"
            :key="index"
        >
            <component
                :is="tabItem"
                :header="(tabItem as VNode)?.props?.header"
                v-bind="(tabItem as VNode)?.props"
                :pt="DsTabPanelPassThroughOptions(props.tabs)"
            />
        </template>
    </TabView>
</template>

<script setup lang="ts">
/** Context: This component is a wrapper for the TabView component.
 * Alongisde DsTabPanel, it provides a consistent PrimeVue styling interface for the TabView component.
 */
import { DsTabPanelPassThroughOptions, type DsTabPanelProps } from '@/components/design-system/tab/DsTabPanel';
import type { TabViewPassThroughOptions, TabViewProps } from 'primevue/tabview';
import TabView from 'primevue/tabview';
import { computed, Fragment, useSlots, type VNode } from 'vue';

const props = defineProps<
    TabViewProps & {
        tabs?: DsTabPanelProps;
    }
>();

const activeIndex = defineModel<number>('activeIndex', { default: 0 });

const slots = useSlots();

/** Captures each node passed into the default slot so they each can be rendered as a Panel */
const panels = computed(() => {
    const nodes = slots.default?.() || [];
    return nodes.flatMap(node => (node.type === Fragment ? node.children : node));
});

const passThrough: TabViewPassThroughOptions = {
    nav: {
        class: 'border-b border-b-ds-primary-200',
        style: {
            borderBottomWidth: '1px !important',
            borderBottomStyle: 'solid !important',
        },
    },
    inkbar: 'p-tabview-ink-bar transition-all absolute bottom-0 duration-300 border border-ds-primary-900',
};
</script>

<style scoped lang="scss">
:deep(li.ds-tab-header) {
    &:hover,
    &:hover a {
        @apply bg-ds-gray-100;
    }
    &:active,
    &:active a {
        @apply bg-ds-gray-200;
    }
    a:focus:not(:focus-visible) {
        --tw-shadow: none !important;
    }
    span {
        animation: none !important;
    }
}
</style>
